/* ag-form-source.js v3 (`agfs`, SPEC 136 + SRM 189b). Shopify prints each
 * contact[Key] of a storefront form as a labelled line in a <pre>. Every form
 * posts Form, Submitted From + Landing Page (origin, path, whitelisted query
 * keys) and Click Ids (one key=value a line); contact[body] is soft-wrapped at
 * 72 columns without touching the field's .value, on three paths sharing
 * wrap(): a window-capture `formdata` listener, fields(key, fd), and wrap().
 * v3 adds contact[Visits]: the visitor's first visit in 90 days, the latest
 * visit that came from an ad, a search, a link or an email, and this session's
 * landing, one key=value a line (the contract is the SRM's
 * app/lead_sources.py docstring). The 90-day part lives in this browser only
 * (localStorage `ag_visits`) and only while Shopify's customer privacy API says
 * analytics is allowed; without it the line carries this session alone.
 * AGFormSource.visits() is the same line for the chat; Calendly embeds get the
 * stored utm_* tags.
 * Laws: window CAPTURE listeners; every storage touch wrapped (Safari private
 * mode throws on access); never block or throw out of a submit; no CR emitted.
 * Record: theme-test/form-source/v3/README.md.
 */
(function (w, d) {
  'use strict';

  var KEY = 'ag_landing';
  var ATTR = 'data-ag-form-key';
  var F_FORM = 'contact[Form]';
  var F_FROM = 'contact[Submitted From]';
  var F_LAND = 'contact[Landing Page]';
  var F_IDS = 'contact[Click Ids]';
  var F_VISITS = 'contact[Visits]';
  var F_BODY = 'contact[body]';
  var MAX_URL = 500;       /* ceiling: each posted URL, and the Click Ids field */
  var MAX_RAW = 2000;      /* the stored landing URL, so its click ids survive */
  var MAX_KEY = 120;
  var WIDTH = 72;
  var KEEP = ['variant', 'utm_source', 'utm_medium', 'utm_campaign',
              'utm_term', 'utm_content'];
  var IDS = ['gclid', 'gbraid', 'wbraid', 'gad_source', 'gad_campaignid',
             'fbclid', 'msclkid', 'ttclid', 'dclid'];

  /* v3: the visits */
  var VKEY = 'ag_visits';  /* localStorage: {v, first, link, offer, offer_at} */
  var OKEY = 'ag_offer';   /* sessionStorage: this session's popup arm */
  var KEEP_MS = 90 * 864e5;
  var MAX_LINE = 1500;     /* the whole Visits line */
  var MAX_VAL = 100;       /* a utm value, before encoding */
  var MAX_PATH = 150;      /* a landing path, before encoding */
  var MAX_ENC = 300;       /* any one encoded value (the SRM keeps 400) */
  var UTM = [['src', 'utm_source'], ['med', 'utm_medium'],
             ['cmp', 'utm_campaign'], ['term', 'utm_term'],
             ['cnt', 'utm_content']];
  var OURS = /(^|\.)(arcgrove\.com|myshopify\.com|shopify\.com|shop\.app)$/;
  var HOST_RE = /^[a-z0-9.-]{1,120}$/;
  var OFFER_RE = /^[a-z0-9_]{1,40}$/;
  var CAL = '.calendly-inline-widget[data-url]';

  function clip(v, n) {
    v = (v === null || v === undefined) ? '' : String(v);
    return v.length > n ? v.slice(0, n) : v;
  }

  function href() {
    try { return String(w.location.href); } catch (e) { return ''; }
  }

  /* A newline at the last space at or before column n; newlines kept; a token
   * longer than n never split. Only spaces become newlines. */
  function wrap(text, n) {
    n = n > 0 ? n : WIDTH;
    var lines = String(text === null || text === undefined ? '' : text)
      .replace(/\r\n?/g, '\n').split('\n');
    var out = [], i, line, lead, cut;
    for (i = 0; i < lines.length; i++) {
      line = lines[i];
      while (line.length > n) {
        lead = line.length - line.replace(/^ +/, '').length;
        cut = line.lastIndexOf(' ', n);
        if (cut <= lead) { cut = line.indexOf(' ', lead); }
        if (cut <= lead) { break; }
        out.push(line.slice(0, cut));
        line = line.slice(cut + 1);
      }
      out.push(line);
    }
    return out.join('\n');
  }

  /* [origin + path, raw k=v pairs], fragment dropped. */
  function parts(u) {
    u = String(u || '');
    var h = u.indexOf('#');
    if (h >= 0) { u = u.slice(0, h); }
    var q = u.indexOf('?');
    return q < 0 ? [u, []] : [u.slice(0, q), u.slice(q + 1).split('&')];
  }

  function keyName(pair) {
    return pair.split('=')[0].toLowerCase();
  }

  /* Origin + path + whitelisted keys; `currency` goes with the rest. */
  function cleanUrl(u) {
    var p = parts(u), kept = [], i;
    for (i = 0; i < p[1].length; i++) {
      if (p[1][i] && KEEP.indexOf(keyName(p[1][i])) >= 0) { kept.push(p[1][i]); }
    }
    return clip(p[0] + (kept.length ? '?' + kept.join('&') : ''), MAX_URL);
  }

  /* Click ids, one key=value a line, first URL wins a key, whole ids only. */
  function clickIds(urls) {
    var seen = {}, out = [], len = 0, i, j, p, pair, eq, k, v, row;
    for (i = 0; i < urls.length; i++) {
      p = parts(urls[i])[1];
      for (j = 0; j < p.length; j++) {
        pair = p[j];
        eq = pair.indexOf('=');
        if (eq < 1) { continue; }
        k = pair.slice(0, eq).toLowerCase();
        v = pair.slice(eq + 1);
        if (IDS.indexOf(k) < 0 || seen[k] || !v || /\s/.test(v)) { continue; }
        row = k + '=' + v;
        if (len + row.length + (out.length ? 1 : 0) > MAX_URL) { continue; }
        len += row.length + (out.length ? 1 : 0);
        seen[k] = true;
        out.push(row);
      }
    }
    return out.join('\n');
  }

  function store() {
    try {
      var s = w.sessionStorage;
      return (s && typeof s.getItem === 'function') ? s : null;
    } catch (e) { return null; }
  }

  function lstore() {
    try {
      var s = w.localStorage;
      return (s && typeof s.getItem === 'function') ? s : null;
    } catch (e) { return null; }
  }

  function readLanding() {
    var s = store();
    if (!s) { return null; }
    try {
      var raw = s.getItem(KEY);
      if (!raw) { return null; }
      var rec = JSON.parse(raw);
      return (rec && typeof rec.url === 'string' && rec.url) ? rec : null;
    } catch (e) { return null; }
  }

  /* The session's first pageview wins, kept raw so its click ids survive. */
  function captureLanding() {
    var have = readLanding();
    if (have) { return have; }
    var rec = {
      url: clip(href(), MAX_RAW),
      ref: clip((d && d.referrer) || '', MAX_URL),
      at: new Date().toISOString()
    };
    var s = store();
    if (s) {
      try { s.setItem(KEY, JSON.stringify(rec)); } catch (e) { /* quota */ }
    }
    return rec;
  }

  /* ---- v3: one visit, out of a landing record {url, ref, at} ---- */

  function dec(v, plus) {
    v = String(v === null || v === undefined ? '' : v);
    if (plus) { v = v.replace(/\+/g, ' '); }
    try { return decodeURIComponent(v); } catch (e) { return v; }
  }

  /* Percent-encoded, never a space; `/ : ,` stay readable. `.` and `@` are
   * encoded too: a mail reader that links `www.google.com` or an address
   * splits the value with a tag, and the SRM then refuses the WHOLE line (its
   * shape test, measured). Capped without cutting an escape in half; '' when
   * the value cannot be encoded. */
  function enc(v, n) {
    var s;
    try {
      s = encodeURIComponent(String(v));
    } catch (e) { return ''; }
    s = s.replace(/[!'()*.]/g, function (c) {
      return '%' + c.charCodeAt(0).toString(16).toUpperCase();
    }).replace(/%2F/g, '/').replace(/%3A/g, ':').replace(/%2C/g, ',');
    if (s.length > n) { s = s.slice(0, n).replace(/%[0-9A-F]?$/, ''); }
    return s;
  }

  /* First value per lower-cased key, decoded. */
  function query(u) {
    var p = parts(u)[1], out = {}, i, eq, k;
    for (i = 0; i < p.length; i++) {
      eq = p[i].indexOf('=');
      if (eq < 1) { continue; }
      k = p[i].slice(0, eq).toLowerCase();
      if (!Object.prototype.hasOwnProperty.call(out, k)) {
        out[k] = dec(p[i].slice(eq + 1), true);
      }
    }
    return out;
  }

  function hostOf(u) {
    var m = /^[a-z][a-z0-9+.\-]*:\/\/(?:[^\/?#@]*@)?([^\/?#:]+)/i.exec(String(u || ''));
    var h = m ? m[1].toLowerCase() : '';
    return HOST_RE.test(h) ? h : '';
  }

  function pathOf(u) {
    var m = /^[a-z][a-z0-9+.\-]*:\/\/[^\/?#]*(\/[^?#]*)?/i.exec(parts(u)[0]);
    return clip(dec((m && m[1]) || '/', false), MAX_PATH);
  }

  function iso(t) {
    var m = /^(\d{4}-\d\d-\d\dT\d\d:\d\d:\d\d)(\.\d+)?Z$/.exec(String(t || ''));
    return m ? m[1] + 'Z' : '';
  }

  function internal(host) {
    var here = '';
    try { here = String(w.location.hostname || '').toLowerCase(); } catch (e) { here = ''; }
    return !!host && (OURS.test(host) || host === here);
  }

  function visitOf(rec) {
    if (!rec || typeof rec.url !== 'string' || !rec.url) { return null; }
    var q = query(rec.url), i, val;
    var v = { at: iso(rec.at), ref: hostOf(rec.ref), path: pathOf(rec.url),
              ids: [] };
    for (i = 0; i < UTM.length; i++) {
      val = clip(q[UTM[i][1]], MAX_VAL).replace(/^\s+|\s+$/g, '');
      if (val) { v[UTM[i][0]] = val; }
    }
    for (i = 0; i < IDS.length; i++) {
      val = q[IDS[i]];
      if (val && !/\s/.test(val)) { v.ids.push(IDS[i]); }
    }
    return v;
  }

  /* From an ad, a search, a link or an email: utm_source / medium, an ad
   * click id, or another site's referrer. Typed in (or our own pages) is not. */
  function fromSomewhere(v) {
    return !!(v && (v.src || v.med || (v.ids && v.ids.length)
                    || (v.ref && !internal(v.ref))));
  }

  function same(a, b) {
    if (!a || !b) { return false; }
    var ks = ['at', 'ref', 'path', 'src', 'med', 'cmp', 'term', 'cnt'], i;
    for (i = 0; i < ks.length; i++) {
      if ((a[ks[i]] || '') !== (b[ks[i]] || '')) { return false; }
    }
    return String(a.ids || '') === String(b.ids || '');
  }

  function young(at, now) {
    var t = Date.parse(at || '');
    return !!t && now - t <= KEEP_MS && t - now < 864e5;
  }

  /* ---- v3: consent. true = may keep the 90-day record; false = may not
   * (and it goes); null = not known yet (nothing written, nothing erased). */
  function consent() {
    try {
      var cp = w.Shopify && w.Shopify.customerPrivacy;
      if (!cp || typeof cp.analyticsProcessingAllowed !== 'function') { return null; }
      return cp.analyticsProcessingAllowed() === true;
    } catch (e) { return null; }
  }

  function readRec() {
    var s = lstore();
    if (!s) { return null; }
    try {
      var r = JSON.parse(s.getItem(VKEY) || 'null'), now = Date.now(), out = { v: 3 };
      if (!r || r.v !== 3) { return null; }
      if (r.first && young(r.first.at, now)) { out.first = r.first; }
      if (r.link && young(r.link.at, now)) { out.link = r.link; }
      if (OFFER_RE.test(r.offer || '') && young(r.offer_at, now)) {
        out.offer = r.offer;
        out.offer_at = r.offer_at;
      }
      return out;
    } catch (e) { return null; }
  }

  function sessionOffer() {
    var s = store();
    if (!s) { return null; }
    try {
      var o = JSON.parse(s.getItem(OKEY) || 'null');
      return (o && OFFER_RE.test(o.offer || '')) ? o : null;
    } catch (e) { return null; }
  }

  /* The session's visit (and offer) into the record. Idempotent. */
  function merge(r, v, o) {
    r = r || { v: 3 };
    if (v && v.at) {
      if (!r.first) { r.first = v; }
      if (fromSomewhere(v) && (!r.link || !r.link.at || v.at > r.link.at)) { r.link = v; }
    }
    if (o && (!r.offer_at || (o.at || '') >= r.offer_at)) {
      r.offer = o.offer;
      r.offer_at = o.at;
    }
    return r;
  }

  function remember() {
    try {
      var c = consent(), s = lstore();
      if (!s || c === null) { return; }
      if (c === false) {
        try { s.removeItem(VKEY); } catch (e) { /* blocked */ }
        return;
      }
      var r = merge(readRec(), visitOf(readLanding()), sessionOffer());
      try { s.setItem(VKEY, JSON.stringify(r)); } catch (e) { /* quota */ }
    } catch (e) { /* attribution never costs the page */ }
  }

  /* The popup arm (`ag_offer`), from a link or AGFormSource.offer(arm). */
  function setOffer(arm) {
    try {
      arm = String(arm || '').toLowerCase();
      if (!OFFER_RE.test(arm)) { return false; }
      var s = store();
      if (s) {
        try {
          s.setItem(OKEY, JSON.stringify({ offer: arm, at: iso(new Date().toISOString()) }));
        } catch (e) { /* quota */ }
      }
      remember();
      return true;
    } catch (e) { return false; }
  }

  function tokens(role, v) {
    var t = [], i, val, ids = [];
    if (!v) { return t; }
    val = iso(v.at);
    if (val) { t.push(role + '_at=' + val); }
    val = String(v.ref || '');
    t.push(role + '_ref=' + (HOST_RE.test(val) ? enc(val, MAX_ENC) : '-'));
    val = v.path ? enc(clip(v.path, MAX_PATH), MAX_ENC) : '';
    if (val) { t.push(role + '_path=' + val); }
    for (i = 0; i < UTM.length; i++) {
      val = v[UTM[i][0]] ? enc(clip(v[UTM[i][0]], MAX_VAL), MAX_ENC) : '';
      if (val) { t.push(role + '_' + UTM[i][0] + '=' + val); }
    }
    for (i = 0; i < IDS.length; i++) {
      if (v.ids && String(',' + v.ids.join(',') + ',').indexOf(',' + IDS[i] + ',') >= 0) {
        ids.push(IDS[i]);
      }
    }
    if (ids.length) { t.push(role + '_ids=' + ids.join(',')); }
    return t;
  }

  function within(rows) {
    var n = 0, i;
    for (i = 0; i < rows.length; i++) { n += rows[i].length + (i ? 1 : 0); }
    return n <= MAX_LINE;
  }

  /* {first, link, last, offer}: the 90-day record (when allowed) merged with
   * this session in memory; without it, this session alone. */
  function roles() {
    var sess = readLanding() || captureLanding();
    var last = visitOf(sess), c = consent(), o = sessionOffer();
    var r = c === false ? null : readRec();
    if (c === true) { remember(); r = readRec(); }
    if (r) { r = merge(r, last, o); }
    return {
      first: r ? (r.first || null) : null,
      link: r ? (r.link || null) : null,
      last: last,
      offer: (o && o.offer) || (r && r.offer) || ''
    };
  }

  /* The Visits line: the first and the last always both (the SRM says "Only
   * visit" when they are one); the link only when it is a third visit. */
  function visits() {
    try {
      var x = roles(), out = [], i, drop;
      var link = (same(x.link, x.first) || same(x.link, x.last)) ? null : x.link;
      out = out.concat(tokens('first', x.first), tokens('link', link),
                       tokens('last', x.last));
      if (OFFER_RE.test(x.offer)) { out.push('offer=' + x.offer); }
      /* over the cap: the free-text tags go first, oldest visit first */
      drop = ['first_cnt', 'link_cnt', 'last_cnt', 'first_term', 'link_term',
              'last_term', 'first_cmp', 'link_cmp', 'first_path', 'link_path'];
      for (i = 0; i < drop.length && !within(out); i++) {
        out = out.filter(function (t) { return t.indexOf(drop[i] + '=') !== 0; });
      }
      while (!within(out)) { out.pop(); }
      return out.join('\n');
    } catch (e) { return ''; }
  }

  function keyOf(form) {
    if (!form || typeof form.getAttribute !== 'function') { return ''; }
    var k = form.getAttribute(ATTR);
    if (k) { return clip(k, MAX_KEY); }
    try {
      var el = form.querySelector('[' + ATTR + ']');
      if (el) { return clip(el.getAttribute(ATTR) || '', MAX_KEY); }
    } catch (e) { /* querySelector on a detached node */ }
    return '';
  }

  function bodyOf(fd) {
    try {
      var b = (fd && typeof fd.get === 'function') ? fd.get(F_BODY) : null;
      return typeof b === 'string' ? b : null;
    } catch (e) { return null; }
  }

  /* For the hand-built FormData paths; with fd, its body rides back wrapped. */
  function fields(key, fd) {
    var land = readLanding() || captureLanding();
    var here = href(), from = (land && land.url) || '';
    var out = {}, ids = clickIds([from, here]), body = bodyOf(fd), line = visits();
    out[F_FORM] = clip(key || '', MAX_KEY);
    out[F_FROM] = cleanUrl(here);
    out[F_LAND] = cleanUrl(from);
    if (ids) { out[F_IDS] = ids; }
    if (line) { out[F_VISITS] = line; }
    if (body !== null) { out[F_BODY] = wrap(body); }
    return out;
  }

  /* Created when absent: Click Ids and Visits have no Liquid input. */
  function setInput(form, name, value) {
    var el = null;
    try {
      el = form.querySelector('input[name="' + name + '"]');
    } catch (e) { el = null; }
    if (!el) {
      if (!d || typeof d.createElement !== 'function') { return; }
      el = d.createElement('input');
      el.type = 'hidden';
      el.name = name;
      try { form.appendChild(el); } catch (e) { return; }
    }
    el.value = value;
  }

  function fill(form, key) {
    try {
      if (!form) { return false; }
      var k = key || keyOf(form);
      if (!k) { return false; }
      var f = fields(k);
      setInput(form, F_FORM, f[F_FORM]);
      setInput(form, F_FROM, f[F_FROM]);
      setInput(form, F_LAND, f[F_LAND]);
      if (f[F_IDS]) { setInput(form, F_IDS, f[F_IDS]); }
      if (f[F_VISITS]) { setInput(form, F_VISITS, f[F_VISITS]); }
      return true;
    } catch (e) { return false; }
  }

  function onSubmit(ev) {
    try {
      var form = ev && ev.target;
      if (!form || form.nodeName !== 'FORM') { return; }
      fill(form);
    } catch (e) { /* never stop a submit */ }
  }

  /* Path a: the entry list, never the field. */
  function onFormData(ev) {
    try {
      var form = ev && ev.target, fd = ev && ev.formData;
      if (!form || form.nodeName !== 'FORM' || !fd || !keyOf(form)) { return; }
      var body = bodyOf(fd);
      if (body !== null) { fd.set(F_BODY, wrap(body)); }
    } catch (e) { /* law 3 */ }
  }

  /* ---- v3: Calendly embeds carry the stored utm_* (its webhook's
   * `tracking`), unless the embed or this page already names some. ---- */
  function calendlyTags() {
    var x = roles(), pick = null, c = [x.link, x.last, x.first], i, q = [];
    for (i = 0; i < c.length && !pick; i++) {
      if (c[i] && (c[i].src || c[i].med || c[i].cmp)) { pick = c[i]; }
    }
    if (!pick) { return ''; }
    for (i = 0; i < UTM.length; i++) {
      if (pick[UTM[i][0]]) {
        q.push(UTM[i][1] + '=' + encodeURIComponent(clip(pick[UTM[i][0]], MAX_VAL)));
      }
    }
    return q.join('&');
  }

  function withTags(u, tags) {
    u = String(u || '');
    if (!tags || /[?&]utm_[a-z]+=/i.test(u)) { return u; }
    var h = u.indexOf('#'), frag = h >= 0 ? u.slice(h) : '';
    u = h >= 0 ? u.slice(0, h) : u;
    return u + (u.indexOf('?') >= 0 ? '&' : '?') + tags + frag;
  }

  function tagCalendly() {
    try {
      if (!d || typeof d.querySelectorAll !== 'function'
          || /[?&]utm_[a-z]+=/i.test(href())) { return; }
      var els = d.querySelectorAll(CAL), tags = '', i, el, fr;
      if (!els.length) { return; }
      tags = calendlyTags();
      if (!tags) { return; }
      for (i = 0; i < els.length; i++) {
        el = els[i];
        if (el.getAttribute('data-agfs-utm')) { continue; }
        el.setAttribute('data-agfs-utm', '1');
        el.setAttribute('data-url', withTags(el.getAttribute('data-url'), tags));
        /* already drawn (its script ran first): point the frame at it once */
        fr = el.querySelector('iframe');
        if (fr && fr.src && !/[?&]utm_[a-z]+=/i.test(fr.src)) {
          fr.src = withTags(fr.src, tags);
        }
      }
    } catch (e) { /* the booking still works without the tags */ }
  }

  /* Law 1: window, capture. */
  try {
    w.addEventListener('submit', onSubmit, true);
    w.addEventListener('formdata', onFormData, true);
  } catch (e) { /* ancient browser: the Liquid fallback values still ship */ }

  captureLanding();
  try {
    var arm = query(href()).ag_offer;
    if (arm) { setOffer(arm); }
  } catch (e) { /* no offer */ }
  remember();
  tagCalendly();
  /* The privacy API may load after this file: try again when it says so. */
  try {
    d.addEventListener('visitorConsentCollected', remember);
    w.addEventListener('load', remember);
  } catch (e) { /* the submit still remembers */ }

  w.AGFormSource = {
    version: 3,
    fields: fields,
    fill: fill,
    key: keyOf,
    landing: readLanding,
    wrap: wrap,
    cleanUrl: cleanUrl,
    clickIds: clickIds,
    visits: visits,
    offer: setOffer,
    names: { form: F_FORM, from: F_FROM, landing: F_LAND, clickIds: F_IDS,
             visits: F_VISITS, body: F_BODY }
  };
}(window, document));
